import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

import { getToken } from '../utils/authToken';
import { isOwnerPremiumEmail } from '../utils/isOwnerPremium';
import { isOwnerAdminEmail } from '../utils/isOwnerAdmin';
import { API_URL } from '../config/api';

// Subcomponentes del Header (refactor P3.1, feb-2026).
import HeaderLogo from './header/HeaderLogo';
import HeaderNav from './header/HeaderNav';
import HeaderCTA from './header/HeaderCTA';
import HeaderAuthButtons from './header/HeaderAuthButtons';
import HeaderMobileMenu from './header/HeaderMobileMenu';
import ConnectionIndicator from './header/ConnectionIndicator';
import ReferralModal from './header/ReferralModal';

// Otros componentes del Header (modals globales y controles transversales).
import ThemeToggle from './ThemeToggle';
import UserProfile from './UserProfile';
import MemoryProfileModal from './MemoryProfileModal';
import SettingsModal from './SettingsModal';
import LearningModePanel from './LearningModePanel';
import InsightsDashboard from './InsightsDashboard';
import AdminUserManagement from './AdminUserManagement';
import AdminTermsManagement from './AdminTermsManagement';
import LanguageSelector from './LanguageSelector';
import MyApps from './MyApps';
import PublicGallery from './PublicGallery';
import HomepageModal from './HomepageModal';
import { PWAInstallButton } from './PWAInstallButton';
// 🟪 P25.6 · Botones movidos desde HeaderLogo a la zona con scroll
// horizontal del Header (mobile/tablet). En desktop siguen visibles
// en la misma línea sin scroll.
import LyonHeaderButton from './LyonHeaderButton';
import DeviceSwitchButton from './DeviceSwitchButton';
import RefreshDashboardButton from './RefreshDashboardButton';
import { MessageQuotaChip } from './MessageQuotaChip';

const API = API_URL;

const Header = ({ onLogout, onLoginClick, isLoggedIn, onOpenConversations, activeProjectFiles = [] }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Estado de modales — mantenido en Header (orquestador).
  const [showMemoryModal, setShowMemoryModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showLearningPanel, setShowLearningPanel] = useState(false);
  const [showInsightsDashboard, setShowInsightsDashboard] = useState(false);
  const [showMyApps, setShowMyApps] = useState(false);
  const [showPublicGallery, setShowPublicGallery] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showTermsAdmin, setShowTermsAdmin] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showHomepageModal, setShowHomepageModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [likesCount, setLikesCount] = useState(0);
  const [liked, setLiked] = useState(false);

  // Verificar si es el creador (whitelist única en isOwnerAdmin.js — incluye
  // support@damyxia.com).
  const userEmail = localStorage.getItem('userEmail') || '';
  const isCreator = isOwnerAdminEmail(userEmail);

  // CUADRO NEGRO feb-2026: las 7 cuentas owner-premium tienen acceso PERMANENTE
  // a todas las features Premium del frontend (decisión de Marcelo).
  // isOwnerPremiumEmail chequea la whitelist contra el email actual sin
  // depender de localStorage.isPremium (que puede quedar stale por race
  // condition al login antes que /auth/me termine).
  const isPremium =
    localStorage.getItem('isPremium') === 'true' ||
    isCreator ||
    isOwnerPremiumEmail(userEmail);

  useEffect(() => {
    if (isLoggedIn) {
      loadLikesCount();
    }
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // FASE 3 — listener global para abrir Settings desde modales de límite Pro.
  useEffect(() => {
    const handler = () => setShowSettingsModal(true);
    window.addEventListener('damyxia:open-settings', handler);
    return () => window.removeEventListener('damyxia:open-settings', handler);
  }, []);

  const loadLikesCount = async () => {
    try {
      const token = getToken();
      if (!token || token === 'guest') return;

      const response = await axios.get(`${API}/likes/count`, {});
      const count = response.data.likes_received || 0;
      setLikesCount(count);

      const hasLiked = localStorage.getItem('damyxia-user-liked');
      if (hasLiked === 'true') {
        setLiked(true);
      }
    } catch (error) {
      console.error('Error loading likes:', error);
    }
  };

  const handleHeartClick = async () => {
    try {
      const token = getToken();
      if (!token || token === 'guest') return;

      if (!liked) {
        await axios.post(`${API}/likes/add`, {}, {});
        setLikesCount((prev) => prev + 1);
        setLiked(true);
        localStorage.setItem('damyxia-user-liked', 'true');
        toast.success('❤️');
      } else {
        await axios.post(`${API}/likes/remove`, {}, {});
        setLikesCount((prev) => Math.max(0, prev - 1));
        setLiked(false);
        localStorage.setItem('damyxia-user-liked', 'false');
        toast.success('💔');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  return (
    <>
      <header
        data-testid="global-header"
        className="fixed top-0 right-0 left-0 z-50 px-1.5 pt-[max(53px,calc(env(safe-area-inset-top)+18px))] pb-0 sm:px-4 sm:pt-[max(55px,calc(env(safe-area-inset-top)+18px))] sm:pb-0.5 flex items-center justify-between gap-1 sm:gap-2 overflow-hidden bg-zinc-950/85 backdrop-blur-md"
      >
        {/* 🟪 P25.6 · ZONA IZQUIERDA FIJA — Logo + ⋮ (no scrollea). */}
        <HeaderLogo isLoggedIn={isLoggedIn} activeProjectFiles={activeProjectFiles} />

        {/* 🟪 P25.6 · ZONA CENTRAL — Scroll horizontal en mobile/tablet.
            Contiene los botones desde Genio hasta Configuración (según
            requerimiento del owner). En desktop hay espacio y no scrollea. */}
        <div
          data-testid="header-scroll-zone"
          className="header-right-scroll flex items-center gap-0.5 sm:gap-2 flex-1 min-w-0 overflow-x-auto"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <style>{`
            .header-right-scroll::-webkit-scrollbar { display: none; }
            /* 🟪 P25.11 · Todos los hijos directos del scroll-zone no
               deben colapsarse — cada botón conserva su ancho natural
               para permanecer tocable. Si el total supera el ancho del
               contenedor, el usuario puede scrollear. */
            .header-right-scroll > * { flex-shrink: 0 !important; }
          `}</style>

          {/* Botones que estaban en HeaderLogo — ahora dentro del scroll. */}
          {isLoggedIn && <LyonHeaderButton />}
          {isLoggedIn && <DeviceSwitchButton />}
          {isLoggedIn && <RefreshDashboardButton />}

          {isLoggedIn && (
            <div className="hidden sm:block">
              <ConnectionIndicator t={t} />
            </div>
          )}

          {/* PWA Install Button - hidden on very small, shown on medium mobile only */}
          <div className="hidden sm:block md:hidden">
            <PWAInstallButton />
          </div>

          <ThemeToggle isPremium={isPremium} />

          {/* 🟪 P25.8 · Separador visual entre ThemeToggle y LanguageSelector.
              En mobile con gap-0.5 quedaban pegados y parecía que la
              bandera USA estaba DENTRO del toggle luna. Este spacer da
              aire real (~4px extra en mobile, ~0px en desktop porque
              ya hay gap-2). */}
          <div className="w-1 sm:w-0 flex-shrink-0" aria-hidden="true" />

          {/* Language Selector - visible también en mobile */}
          <div className="block flex-shrink-0">
            <LanguageSelector />
          </div>

          {/* Navegación de iconos centrales (solo si logueado). */}
          <HeaderNav
            isLoggedIn={isLoggedIn}
            liked={liked}
            onHeartClick={handleHeartClick}
            onOpenMyApps={() => setShowMyApps(true)}
            onOpenPublicGallery={() => setShowPublicGallery(true)}
          />

          {/* Badges de plan (Creator / Premium). */}
          <HeaderCTA isLoggedIn={isLoggedIn} isCreator={isCreator} isPremium={isPremium} />

          {/* Quota chip — visible en desktop; mobile lo tiene en el menú ⋮. */}
          {isLoggedIn && (
            <div className="hidden sm:flex">
              <MessageQuotaChip />
            </div>
          )}

          {/* Solo Configuración dentro del scroll — Logout va fuera. */}
          <HeaderAuthButtons
            isLoggedIn={isLoggedIn}
            onLogout={onLogout}
            onOpenSettings={() => setShowSettingsModal(true)}
            only="settings"
          />
        </div>

        {/* 🟪 P25.6 · ZONA DERECHA FIJA — Login/Logout (no scrollea). */}
        <div className="flex-shrink-0">
          <HeaderAuthButtons
            isLoggedIn={isLoggedIn}
            onLogout={onLogout}
            onOpenSettings={() => setShowSettingsModal(true)}
            only="auth"
          />
        </div>
      </header>

      {/* Mobile menu — hoy placeholder (Header se adapta sin hamburguesa). */}
      <HeaderMobileMenu
        isLoggedIn={isLoggedIn}
        onLogout={onLogout}
        open={mobileMenuOpen}
        setOpen={setMobileMenuOpen}
      />

      <MemoryProfileModal
        isOpen={showMemoryModal}
        onClose={() => {
          setShowMemoryModal(false);
          setShowSettingsModal(true);
        }}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onLogout={onLogout}
        onOpenShare={() => setShowReferralModal(true)}
        onOpenMemory={() => setShowMemoryModal(true)}
        onOpenLearning={() => setShowLearningPanel(true)}
        onOpenInsights={() => setShowInsightsDashboard(true)}
        onOpenConversations={onOpenConversations}
        onOpenProfile={() => setShowProfileModal(true)}
        onOpenAdmin={() => setShowAdminPanel(true)}
        onOpenTermsAdmin={() => setShowTermsAdmin(true)}
      />

      {/* Referral Modal — extraído a /header/ReferralModal.jsx */}
      <ReferralModal
        isOpen={showReferralModal}
        onClose={() => {
          setShowReferralModal(false);
          setShowSettingsModal(true);
        }}
      />

      {/* Learning Mode Panel */}
      <LearningModePanel
        isOpen={showLearningPanel}
        onClose={() => {
          setShowLearningPanel(false);
          setShowSettingsModal(true);
        }}
      />

      {/* Insights Dashboard */}
      <InsightsDashboard
        isOpen={showInsightsDashboard}
        onClose={() => {
          setShowInsightsDashboard(false);
          setShowSettingsModal(true);
        }}
      />

      {/* Admin User Management Panel */}
      <AdminUserManagement
        isOpen={showAdminPanel}
        onClose={() => {
          setShowAdminPanel(false);
          setShowSettingsModal(true);
        }}
      />

      {/* Admin Terms Management Panel */}
      <AdminTermsManagement
        isOpen={showTermsAdmin}
        onClose={() => {
          setShowTermsAdmin(false);
          setShowSettingsModal(true);
        }}
      />

      {/* My Apps Modal */}
      <MyApps isOpen={showMyApps} onClose={() => setShowMyApps(false)} />

      {/* Public Gallery Modal */}
      <PublicGallery isOpen={showPublicGallery} onClose={() => setShowPublicGallery(false)} />

      {/* Homepage Modal - Guía de DamyxIA */}
      <HomepageModal isOpen={showHomepageModal} onClose={() => setShowHomepageModal(false)} />

      {/* User Profile Modal - Abierto desde Settings */}
      {showProfileModal && (
        <UserProfile
          isLoggedIn={isLoggedIn}
          externalOpen={showProfileModal}
          onExternalClose={() => {
            setShowProfileModal(false);
            setShowSettingsModal(true);
          }}
        />
      )}
    </>
  );
};

export default Header;
